import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  TextField,
  Typography,
  Checkbox,
  Popper,
  Paper,
  List,
  ListItemButton,
  CircularProgress,
  InputAdornment,
  Button,
  useTheme,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { FilterValue } from "../../../../models/Filters/FilterValue";

export type AppSearchDropdownMultiProps = {
  isMultiSelect?: boolean;
  title: string;
  value?: FilterValue[];
  list: FilterValue[];
  local?: boolean;
  fontSize?: string;
  handleSearch?: (searchText?: string) => void;
  handleSelect?: (selectedItems: FilterValue[]) => void;
  onOpen?: () => void;
  disabled?: boolean;
  minWidth?: string;
  borderColor?: string;
  error?: boolean;
  errorMessage?: string;
  showBorder?: boolean;
  backgroundColor?: string;
  inputHeight?: string;
  borderRadius?: string;
  showId?: boolean;
  accentColor?: string;
  /** When a value is selected, show it inside the field and place `title` on the top-left border. */
  floatingLabel?: boolean;
  /** Controlled loading state for remote search; overrides the internal keystroke-based guess. */
  loading?: boolean;
  /** Shows an "add new" action under the options; called with the current search text. */
  onCreateNew?: (searchText: string) => void;
  /** Label for the "add new" action. Defaults to `+ Add "<search>"` / `+ Add new`. */
  createNewLabel?: (searchText: string) => string;
};

function optionId(option: FilterValue) {
  return `${option.id}-${option.value}`;
}

function isOptionSelected(option: FilterValue, selected: FilterValue[]) {
  return selected.some((v) => String(v.id) === String(option.id));
}

/**
 * Multi-select search dropdown (no Autocomplete).
 * Trigger shows `title` when empty. With `floatingLabel`, a selected value is shown
 * inside the field and `title` sits on the top-left border. Otherwise the trigger
 * prefixes the title (`Title: value` or `Title: N values selected`).
 */
export default function AppSearchDropdownMulti({
  isMultiSelect = true,
  title,
  list,
  value = [],
  local = false,
  fontSize = "0.8rem",
  handleSearch,
  handleSelect,
  onOpen,
  disabled = false,
  minWidth,
  borderColor = "#ddd",
  error = false,
  errorMessage = "This is a required field",
  showBorder = true,
  backgroundColor,
  inputHeight = "37px",
  borderRadius = "4px",
  showId = false,
  accentColor = "#6455ED",
  floatingLabel = false,
  loading: loadingProp,
  onCreateNew,
  createNewLabel,
}: AppSearchDropdownMultiProps) {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [internalLoading, setLoading] = useState(false);
  const loading = loadingProp ?? internalLoading;
  const anchorRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const selected = value ?? [];

  // Options to show: when local, filter list by searchText; otherwise use list from API
  const displayList = local
    ? (() => {
        if (!searchText.trim()) return list;
        const pattern = new RegExp(
          searchText.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
          "i",
        );
        return list.filter(
          (item) =>
            pattern.test(String(item.value)) ||
            (item.subtitle ? pattern.test(String(item.subtitle)) : false),
        );
      })()
    : list;

  useEffect(() => {
    if (!local && open) {
      setLoading(false);
    }
  }, [list, local, open]);

  useEffect(() => {
    if (open) {
      onOpen?.();
      const timer = setTimeout(() => searchInputRef.current?.focus(), 0);
      return () => clearTimeout(timer);
    } else {
      setSearchText("");
    }
  }, [open, onOpen]);

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      const anchor = anchorRef.current;
      const popper = document.getElementById(
        "app-search-dropdown-multi-popper",
      );
      if (
        anchor &&
        popper &&
        !anchor.contains(e.target as Node) &&
        !popper.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const handleTriggerClick = () => {
    if (disabled) return;
    setOpen((prev) => !prev);
    if (!open && !local) {
      handleSearch?.("");
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setSearchText(v);
    if (!local) {
      setLoading(true);
    }
    handleSearch?.(v);
  };

  const toggleOption = (option: FilterValue) => {
    if (!handleSelect) return;
    const isSelected = selected.some((v) => String(v.id) === String(option.id));
    let next: FilterValue[];
    if (isSelected) {
      next = selected.filter((v) => String(v.id) !== String(option.id));
    } else {
      if (isMultiSelect) {
        next = [...selected, option];
      } else {
        next = [option];
      }
    }
    handleSelect(next);
    if (!isMultiSelect) {
      setOpen(false);
    }
  };

  const handleSelectAll = () => {
    if (!handleSelect || !isMultiSelect) return;
    // Select all options from displayList (filtered list when searching)
    const allDisplaySelected = displayList.filter(
      (option) => !isOptionSelected(option, selected),
    );
    const newSelection = [...selected, ...allDisplaySelected];
    handleSelect(newSelection);
  };

  const handleDeselectAll = () => {
    if (!handleSelect || !isMultiSelect) return;
    // Deselect all options from displayList (filtered list when searching)
    const newSelection = selected.filter(
      (selectedOption) =>
        !displayList.some(
          (displayOption) =>
            String(displayOption.id) === String(selectedOption.id),
        ),
    );
    handleSelect(newSelection);
  };

  // Check if all displayList items are selected
  const allDisplaySelected =
    displayList.length > 0 &&
    displayList.every((option) => isOptionSelected(option, selected));
  const someDisplaySelected = displayList.some((option) =>
    isOptionSelected(option, selected),
  );

  const selectedValueLabel = (() => {
    try {
      if (selected.length === 0) {
        return "";
      }
      if (!isMultiSelect && selected.length === 1) {
        return (
          selected[0].value ||
          list.find((item) => String(item.id) === String(selected[0].id))
            ?.value ||
          ""
        );
      }
      return `${selected.length} value${selected.length === 1 ? "" : "s"} selected`;
    } catch (error) {
      return "";
    }
  })();

  const hasSelection = selected.length > 0;
  const showFloatingLabel = floatingLabel && hasSelection;
  const fieldBackground = disabled
    ? "#f5f5f5"
    : backgroundColor || "#fff";
  const triggerLabel = showFloatingLabel
    ? selectedValueLabel
    : selectedValueLabel
      ? title.trim()
        ? `${title}: ${selectedValueLabel}`
        : selectedValueLabel
      : title;

  return (
    <Box
      sx={{
        minWidth: minWidth ?? "80px",
        width: "100%",
        maxWidth: "100%",
        flexShrink: 0,
        position: "relative",
        zIndex: open ? 9100 : 1,
        pt: floatingLabel ? "7px" : 0,
        overflow: "visible",
      }}
    >
      {showFloatingLabel && (
        <Typography
          component="span"
          sx={{
            position: "absolute",
            top: "7px",
            left: "10px",
            transform: "translateY(-50%)",
            px: "4px",
            maxWidth: "calc(100% - 36px)",
            fontSize: "11px",
            lineHeight: 1.2,
            fontWeight: 500,
            color: error ? "#d32f2f" : open ? accentColor : "#6B7280",
            bgcolor: fieldBackground,
            zIndex: 2,
            pointerEvents: "none",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {title}
        </Typography>
      )}
      {/* Main box: title, selected value, or "title: selected" + dropdown button */}
      <Box
        ref={anchorRef}
        onClick={handleTriggerClick}
        sx={{
          minHeight: inputHeight,
          minWidth: "0",
          width: "100%",
          boxSizing: "border-box",
          borderRadius,
          border: showBorder
            ? `1px solid ${error ? "#d32f2f" : borderColor}`
            : "none",
          backgroundColor: fieldBackground,
          cursor: disabled ? "default" : "pointer",
          display: "flex",
          alignItems: "center",
          padding: "6px 8px",
          position: "relative",
          opacity: disabled ? 0.75 : 1,
          overflow: "hidden",
          "&:hover": {
            borderColor: disabled ? undefined : error ? "#d32f2f" : accentColor,
          },
          "&:focus-within": {
            outline: "none",
            borderColor: disabled ? undefined : error ? "#d32f2f" : accentColor,
          },
        }}
      >
        <Typography
          component="span"
          sx={{
            display: "block",
            width: "calc(100% - 30px)",
            fontSize,
            color:
              selected.length === 0
                ? "#93969B"
                : error
                  ? "#d32f2f"
                  : "#000000",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {triggerLabel}
        </Typography>
        <ArrowDropDownIcon
          sx={{
            position: "absolute",
            right: 8,
            top: "50%",
            transform: "translateY(-50%)",
            color: disabled ? "#bdbdbd" : "#93969B",
            pointerEvents: "none",
            flexShrink: 0,
          }}
        />
      </Box>

      {/* Dropdown: search + list */}
      <Popper
        id="app-search-dropdown-multi-popper"
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        style={{ zIndex: 9100 }}
        modifiers={[
          { name: "offset", options: { offset: [0, 4] } },
          { name: "preventOverflow", options: { padding: 8 } },
        ]}
      >
        <Paper
          elevation={8}
          sx={{
            minWidth: anchorRef.current?.offsetWidth ?? 280,
            maxWidth: 400,
            maxHeight: 320,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            borderRadius: "8px",
            paddingBottom: "6px",
          }}
        >
          {/* Search bar */}
          <Box
            sx={{
              p: 1.5,
              borderBottom: "1px solid",
              borderColor: "divider",
              backgroundColor: "#fff",
            }}
          >
            <TextField
              inputRef={searchInputRef}
              size="small"
              placeholder="Search..."
              value={searchText}
              onChange={handleSearchChange}
              fullWidth
              variant="outlined"
              slotProps={{
                input: {
                  sx: { fontSize },
                  endAdornment: loading ? (
                    <InputAdornment position="end">
                      <CircularProgress size={18} />
                    </InputAdornment>
                  ) : undefined,
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#fff",
                },
              }}
            />
          </Box>

          {/* Select All / Deselect All buttons */}
          {isMultiSelect && displayList.length > 0 && (
            <Box
              sx={{
                px: 1.5,
                py: 1,
                borderBottom: "1px solid",
                borderColor: "divider",
                display: "flex",
                gap: 1,
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Button
                size="small"
                onClick={handleSelectAll}
                disabled={allDisplaySelected}
                sx={{
                  flex: 1,
                  textTransform: "none",
                  fontSize: "0.75rem",
                  px: 1.5,
                  py: 0.5,
                  color: "primary.main",
                  "&:hover": {
                    backgroundColor: "action.hover",
                  },
                  "&:disabled": {
                    color: "text.disabled",
                  },
                }}
              >
                Select All
              </Button>
              <Button
                size="small"
                onClick={handleDeselectAll}
                disabled={!someDisplaySelected}
                sx={{
                  flex: 1,
                  textTransform: "none",
                  fontSize: "0.75rem",
                  px: 1.5,
                  py: 0.5,
                  color: "primary.main",
                  "&:hover": {
                    backgroundColor: "action.hover",
                  },
                  "&:disabled": {
                    color: "text.disabled",
                  },
                }}
              >
                Deselect All
              </Button>
            </Box>
          )}

          {/* Option list */}
          <List
            dense
            sx={{
              overflow: "auto",
              maxHeight: 260,
              py: 0,
              "& .MuiListItemButton-root": {
                py: 0.5,
                backgroundColor: "transparent",
              },
              "& .MuiListItemButton-root:hover": {
                backgroundColor: "action.hover",
              },
            }}
          >
            {displayList.length === 0 && (
              <Box sx={{ py: 2, textAlign: "center" }}>
                <Typography sx={{ fontSize, color: "text.secondary" }}>
                  {loading ? "Loading..." : "No options"}
                </Typography>
              </Box>
            )}
            {displayList.map((option) => {
              const selected_ = isOptionSelected(option, selected);
              return (
                <ListItemButton
                  key={optionId(option)}
                  onClick={() => toggleOption(option)}
                  disableRipple
                  sx={{ alignItems: "flex-start" }}
                >
                  <Checkbox
                    icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                    checkedIcon={<CheckBoxIcon fontSize="small" />}
                    checked={selected_}
                    size="small"
                    sx={{ mr: 1, p: 0.5, mt: option.subtitle ? 0.25 : 0 }}
                  />
                  <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column" }}>
                    <Typography
                      component="span"
                      sx={{
                        fontSize,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {showId ? `${option.id} - ${option.value}` : option.value}
                    </Typography>
                    {option.subtitle ? (
                      <Typography
                        component="span"
                        sx={{
                          fontSize: "0.7rem",
                          color: theme.palette.customColors.greyTextColor,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {option.subtitle}
                      </Typography>
                    ) : null}
                  </Box>
                </ListItemButton>
              );
            })}
          </List>

          {onCreateNew && (
            <Box
              sx={{
                px: 1,
                pt: 0.75,
                borderTop: "1px solid",
                borderColor: "divider",
              }}
            >
              <Button
                size="small"
                fullWidth
                onClick={() => {
                  const text = searchText.trim();
                  setOpen(false);
                  onCreateNew(text);
                }}
                sx={{
                  justifyContent: "flex-start",
                  textTransform: "none",
                  fontSize,
                  fontWeight: 600,
                  color: accentColor,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {createNewLabel
                  ? createNewLabel(searchText.trim())
                  : searchText.trim()
                    ? `+ Add "${searchText.trim()}"`
                    : "+ Add new"}
              </Button>
            </Box>
          )}
        </Paper>
      </Popper>

      {error && (
        <Typography
          sx={{ color: "#d32f2f", fontSize: "0.75rem", marginTop: "4px" }}
        >
          {errorMessage}
        </Typography>
      )}
    </Box>
  );
}
